// 📂 src/pages/CameraDetail/components/SnapshotButton.jsx
// Enterprise screenshot capture — frame grab with overlay, matching VMS icon style

import { useState, useCallback } from "react";
import { FiCamera, FiCheck } from "react-icons/fi";

export default function SnapshotButton({
  videoRef,
  camera,
  currentTime,
  timezone = "America/Vancouver",
  disabled = false,
}) {
  const [state, setState] = useState("idle"); // idle | capturing | done

  const takeSnapshot = useCallback(async () => {
    if (state !== "idle" || disabled) return;

    // Try to get the video element — it might be nested inside a component
    let video = videoRef?.current;
    if (!video) {
      // Fallback: find any video element in the DOM
      video = document.querySelector("video");
    }
    if (!video) return;

    setState("capturing");

    try {
      const canvas = document.createElement("canvas");
      const vw = video.videoWidth || video.clientWidth || 1920;
      const vh = video.videoHeight || video.clientHeight || 1080;
      canvas.width = vw;
      canvas.height = vh;
      const ctx = canvas.getContext("2d");

      // Draw the current frame
      ctx.drawImage(video, 0, 0, vw, vh);

      // Overlay background strip at bottom
      const stripH = Math.max(32, vh * 0.035);
      ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
      ctx.fillRect(0, vh - stripH, vw, stripH);

      // Font sizing
      const fontSize = Math.max(12, Math.floor(stripH * 0.5));
      ctx.font = `${fontSize}px "SF Mono", "Cascadia Code", "Consolas", monospace`;
      ctx.textBaseline = "middle";
      const textY = vh - stripH / 2;
      const pad = Math.max(10, vw * 0.01);

      // Timestamp (left)
      const ts = currentTime
        ? new Date(currentTime).toLocaleString("en-CA", {
            year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
            hour12: false, timeZone: timezone,
          })
        : new Date().toLocaleString("en-CA", {
            year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
            hour12: false, timeZone: timezone,
          });

      ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
      ctx.textAlign = "left";
      ctx.fillText(ts, pad, textY);

      // Camera name (right)
      const camName = camera?.name || "Camera";
      ctx.textAlign = "right";
      ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
      ctx.fillText(camName, vw - pad, textY);

      // Small ViewMax watermark (far right, very subtle)
      ctx.font = `${Math.max(9, Math.floor(fontSize * 0.6))}px sans-serif`;
      ctx.fillStyle = "rgba(255, 255, 255, 0.25)";
      ctx.textAlign = "right";
      ctx.fillText("ViewMax", vw - pad, vh - stripH - 4);

      // Download the image
      const dataUrl = canvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      const safeName = (camera?.name || "camera").replace(/[^a-zA-Z0-9_-]/g, "_");
      const safeTs = (currentTime || new Date())
        .toISOString()
        .slice(0, 19)
        .replace(/[T:]/g, "-");
      link.download = `${safeName}_${safeTs}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setState("done");
      setTimeout(() => setState("idle"), 2000);
    } catch (err) {
      console.error("Snapshot failed:", err);
      setState("idle");
    }
  }, [videoRef, camera, currentTime, timezone, state, disabled]);

  return (
    <button
      onClick={takeSnapshot}
      disabled={disabled || state === "capturing"}
      className={`vms-icon-btn ${
        state === "done"
          ? "!bg-green-500/20 !text-green-400"
          : ""
      }`}
      title="Take snapshot (S)"
    >
      {state === "done" ? (
        <FiCheck className="w-4 h-4" />
      ) : (
        <FiCamera className="w-4 h-4" />
      )}
    </button>
  );
}
